(function () {
  window.SharedTournamentModalRenderer = function renderTournamentModal(context) {
    const {
      h,
      tournamentProfileId,
      setTournamentProfileId,
      tournamentLocationFormQuery,
      setTournamentLocationFormQuery,
      tournamentLocationFormStateFilter,
      setTournamentLocationFormStateFilter,
      tournamentLocationFormCityFilter,
      setTournamentLocationFormCityFilter,
      tournamentForm,
      setTournamentForm,
      tournamentGalleryPreviews,
      tournamentPendingUploads,
      handleTournamentGallerySelect,
      removeTournamentGalleryImage,
      reorderTournamentGallery,
      tournamentSaveStatus,
      submitTournamentForm,
      deleteTournament,
      tournamentDeleteConfirmOpen,
      setTournamentDeleteConfirmOpen,
      confirmDeleteTournament,
      tournamentModalOpen,
      closeTournamentModal,
      tournamentStep,
      setTournamentStep,
      locationStateOptions,
      locationCityOptions,
      filteredProfiles,
      ratingOptions,
      daysOfWeek,
      skillLevelOptions,
      getStateDisplayName,
      getStateAbbrev,
      normalizeRatingSystemType,
      isGlobalCustomer,
      tournamentFieldErrors,
      setTournamentFieldErrors,
    } = context;

    if (!tournamentModalOpen) return null;
    const isSaving = /^saving\b/i.test(String(tournamentSaveStatus || ""));
    const safeFieldErrors =
      tournamentFieldErrors && typeof tournamentFieldErrors === "object"
        ? tournamentFieldErrors
        : {};

    const getFieldError = (key) => String(safeFieldErrors[key] || "").trim();
    const hasFieldError = (key) => Boolean(getFieldError(key));
    const getFieldClassName = (key) => `hd-field${hasFieldError(key) ? " hd-field--error" : ""}`;
    const getInputClassName = (key) => `hd-input${hasFieldError(key) ? " hd-input--error" : ""}`;
    const renderFieldError = (key) => {
      const message = getFieldError(key);
      if (!message) return null;
      return h("div", { className: "hd-field-error" }, message);
    };

    const clearFieldError = (key) => {
      if (typeof setTournamentFieldErrors !== "function") return;
      setTournamentFieldErrors((prev) => {
        if (!prev || !Object.prototype.hasOwnProperty.call(prev, key)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    };

    const statusText = String(tournamentSaveStatus || "").trim();
    const statusClassName = (() => {
      const lower = statusText.toLowerCase();
      const classes = ["hd-save-status", "hd-save-status--banner"];
      if (
        /^error\b/.test(lower)
        || lower.startsWith("missing")
        || lower.startsWith("select")
        || lower.startsWith("please")
        || lower.startsWith("failed")
        || lower.includes("must be")
      ) {
        classes.push("hd-save-status--error");
      } else if (lower.includes("created") || lower.includes("saved")) {
        classes.push("hd-save-status--success");
      } else {
        classes.push("hd-save-status--info");
      }
      return classes.join(" ");
    })();

    const updateForm = (key, value) => {
      setTournamentForm((prev) => ({ ...prev, [key]: value }));
      clearFieldError(key);
      if (key === "locationName") clearFieldError("profileId");
    };

    const updateSpec = (key, value) => {
      setTournamentForm((prev) => ({
        ...prev,
        specificationData: {
          ...(prev.specificationData || {}),
          tournaments: {
            ...(prev.specificationData?.tournaments || {}),
            [key]: value,
          },
        },
      }));
      if (key === "registration_start" || key === "registration_end" || key === "entry_deadline") {
        clearFieldError("registrationRange");
      }
    };

    const duplicateCurrentTournament = () => {
      setTournamentForm((prev) => ({
        ...prev,
        id: "",
        applyToSeries: false,
      }));
      if (typeof setTournamentDeleteConfirmOpen === "function") {
        setTournamentDeleteConfirmOpen(false);
      }
    };

    const toDateTimeLocalValue = (value) => {
      const raw = String(value || "").trim();
      if (!raw) return "";
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return raw;
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return `${raw}T00:00`;

      const parsed = new Date(raw);
      if (!Number.isFinite(parsed.getTime())) return "";

      const pad = (num) => String(num).padStart(2, "0");
      return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    };

    const splitDateTimeParts = (value) => {
      const localValue = toDateTimeLocalValue(value);
      if (!localValue) return { date: "", time: "" };
      const [datePart, timePart = ""] = localValue.split("T");
      return {
        date: datePart || "",
        time: String(timePart || "").slice(0, 5),
      };
    };

    const combineDateTimeParts = (datePart, timePart, defaultTime = "23:59") => {
      const date = String(datePart || "").trim();
      if (!date) return "";
      const time = /^\d{2}:\d{2}$/.test(String(timePart || "").trim())
        ? String(timePart || "").trim()
        : defaultTime;
      return `${date}T${time}`;
    };

    const spec = tournamentForm.specificationData?.tournaments || {};
    const gameOptions = [
      "8 Ball",
      "9 Ball",
      "10 Ball",
      "Straight Pool",
      "One Pocket",
      "Bank Pool",
      "Snooker",
    ];
    const tableOptions = ["7'", "8'", "9'"];
    const normalizeTableSizeValue = (value) => {
      const raw = String(value || "").trim();
      if (!raw) return "";
      const normalized = raw.toLowerCase().replace(/\s+/g, " ");
      if (normalized === "7'" || normalized === "7 foot" || normalized === "7-foot" || normalized.includes("7 foot")) return "7'";
      if (normalized === "8'" || normalized === "8 foot" || normalized === "8-foot" || normalized.includes("8 foot")) return "8'";
      if (normalized === "9'" || normalized === "9 foot" || normalized === "9-foot" || normalized.includes("9 foot")) return "9'";
      return raw;
    };

    const createDefaultBracket = (index) => ({
      name: `Bracket ${index + 1}`,
      ratingMode: "to",
      minRating: "",
      maxRating: "",
      gameType: "",
      losersGameType: "",
      apaLevels: [],
      tableSizes: [],
      maxPlayers: "",
      winnerRaces: "",
      loserRaces: "",
      raceMayVaryBySkillLevel: false,
    });

    const resolveWinnerGameType = (bracket) =>
      bracket?.gameType || bracket?.game_type || bracket?.winnerGameType || bracket?.winner_game_type || "";

    const resolveLoserGameType = (bracket, winnerGameType) =>
      bracket?.losersGameType ||
      bracket?.loserGameType ||
      bracket?.losers_game_type ||
      bracket?.loser_game_type ||
      "";

    const divisions = (() => {
      if (Array.isArray(spec.divisions) && spec.divisions.length > 0) {
        return spec.divisions.map((division, index) => ({
          ...(division || {}),
          name: division?.name || `Division ${index + 1}`,
          divisionType: division?.divisionType || division?.division_type || "",
          formatType: division?.formatType || division?.format_type || "",
          handicapMethod:
            division?.handicapMethod || division?.handicap_method || division?.handicap || "",
          combinedSkillCap: division?.combinedSkillCap || division?.combined_skill_cap || "",
          brackets: Array.isArray(division?.brackets) ? division.brackets : [],
        }));
      }

      if (Array.isArray(spec.brackets) && spec.brackets.length > 0) {
        return [
          {
            name: "Division 1",
            brackets: spec.brackets,
          },
        ];
      }

      return [];
    })();

    const flattenBrackets = (nextDivisions) =>
      (Array.isArray(nextDivisions) ? nextDivisions : []).flatMap((division) =>
        Array.isArray(division?.brackets) ? division.brackets : []
      );

    const updateDivisions = (nextDivisions) => {
      const sanitized = (Array.isArray(nextDivisions) ? nextDivisions : []).map((division, index) => {
        const divisionType = String(division?.divisionType ?? division?.division_type ?? "").trim();
        const formatType = String(division?.formatType ?? division?.format_type ?? "").trim();
        const handicapMethod = String(
          division?.handicapMethod ?? division?.handicap_method ?? division?.handicap ?? ""
        ).trim();
        const combinedSkillCap = String(
          division?.combinedSkillCap ?? division?.combined_skill_cap ?? ""
        ).trim();

        return {
          ...(division || {}),
          name: String(division?.name || `Division ${index + 1}`).trim(),
          divisionType,
          division_type: divisionType,
          formatType,
          format_type: formatType,
          handicapMethod,
          handicap_method: handicapMethod,
          handicap: handicapMethod,
          combinedSkillCap,
          combined_skill_cap: combinedSkillCap,
          brackets: (Array.isArray(division?.brackets) ? division.brackets : []).map((bracket, bracketIndex) => {
            const winnerGameType = resolveWinnerGameType(bracket);
            const loserGameType = resolveLoserGameType(bracket, winnerGameType);
            return {
              ...createDefaultBracket(bracketIndex),
              ...(bracket || {}),
              gameType: winnerGameType,
              losersGameType: loserGameType,
            };
          }),
        };
      });
      updateSpec("divisions", sanitized);
      // Keep legacy readers working until all templates/routes are fully division-aware.
      updateSpec("brackets", flattenBrackets(sanitized));
    };

    const addDivision = () => {
      const defaultHandicapMethod = String(
        spec.handicap_method || spec.handicapMethod || spec.handicap || "none"
      ).trim() || "none";
      const next = divisions.concat({
        name: `Division ${divisions.length + 1}`,
        divisionType: "",
        formatType: "",
        handicapMethod: defaultHandicapMethod,
        combinedSkillCap: "",
        brackets: [],
      });
      updateDivisions(next);
    };

    const divisionTypeOptions = [
      { value: "", label: "Select type" },
      { value: "singles", label: "Singles" },
      { value: "doubles", label: "Doubles" },
      { value: "teams", label: "Teams" },
      { value: "scotch_doubles", label: "Scotch Doubles" },
      { value: "other", label: "Other" },
    ];

    const formatTypeOptions = [
      { value: "", label: "Select format" },
      { value: "single_elimination", label: "Single Elimination" },
      { value: "double_elimination", label: "Double Elimination" },
      { value: "round_robin", label: "Round Robin" },
      { value: "race_chart", label: "Race Chart" },
      { value: "swiss", label: "Swiss" },
      { value: "league_format", label: "League Format" },
      { value: "custom", label: "Custom" },
    ];

    const handicapMethodOptions = [
      { value: "none", label: "None" },
      { value: "chip", label: "Chip" },
      { value: "race_chart", label: "Race Chart" },
      { value: "spot_balls", label: "Spot Balls" },
      { value: "spot_games", label: "Spot Games" },
      { value: "fargo_race", label: "Fargo Race" },
      { value: "apa_chart", label: "APA Chart" },
      { value: "custom", label: "Custom" },
    ];

    const sideActionOptions = [
      { value: "calcutta", label: "Calcutta" },
      { value: "player_auction", label: "Player Auction" },
      { value: "ring_game", label: "Ring Game" },
      { value: "mini_tournament", label: "Mini Tournament" },
      { value: "money_matches", label: "Money Matches" },
    ];

    const removeDivision = (divisionIndex) => {
      updateDivisions(divisions.filter((_, i) => i !== divisionIndex));
    };

    const getTournamentFormatType = () => {
      const directValue = String(spec.format_type || spec.formatType || "").trim();
      if (directValue) return directValue;
      const firstDivisionValue = divisions
        .map((division) => String(division?.formatType || division?.format_type || "").trim())
        .find(Boolean);
      return firstDivisionValue || "";
    };

    const setTournamentFormatType = (value) => {
      const normalizedValue = String(value || "").trim();
      updateSpec("format_type", normalizedValue);
      updateSpec("formatType", normalizedValue);

      if (divisions.length > 0) {
        const nextDivisions = divisions.map((division) => ({
          ...(division || {}),
          formatType: normalizedValue,
          format_type: normalizedValue,
        }));
        updateDivisions(nextDivisions);
      }
    };

    const getTournamentHandicapMethod = () => {
      const directValue = String(spec.handicap_method || spec.handicapMethod || spec.handicap || "").trim();
      if (directValue) return directValue;
      const firstDivisionValue = divisions
        .map((division) => String(division?.handicapMethod || division?.handicap_method || division?.handicap || "").trim())
        .find(Boolean);
      return firstDivisionValue || "none";
    };

    const setTournamentHandicapMethod = (value) => {
      const normalizedValue = String(value || "").trim() || "none";
      updateSpec("handicap_method", normalizedValue);
      updateSpec("handicapMethod", normalizedValue);
      updateSpec("handicap", normalizedValue);

      if (divisions.length > 0) {
        const nextDivisions = divisions.map((division) => ({
          ...(division || {}),
          handicapMethod: normalizedValue,
          handicap_method: normalizedValue,
          handicap: normalizedValue,
        }));
        updateDivisions(nextDivisions);
      }
    };

    const updateDivision = (divisionIndex, key, value) => {
      const next = divisions.map((division, i) => {
        if (i !== divisionIndex) return division;
        return { ...(division || {}), [key]: value };
      });
      updateDivisions(next);
    };

    const addBracket = (divisionIndex) => {
      const next = divisions.map((division, i) => {
        if (i !== divisionIndex) return division;
        const existingBrackets = Array.isArray(division?.brackets) ? division.brackets : [];
        return {
          ...(division || {}),
          brackets: existingBrackets.concat(createDefaultBracket(existingBrackets.length)),
        };
      });
      updateDivisions(next);
    };

    const removeBracket = (divisionIndex, bracketIndex) => {
      const next = divisions.map((division, i) => {
        if (i !== divisionIndex) return division;
        const existingBrackets = Array.isArray(division?.brackets) ? division.brackets : [];
        return {
          ...(division || {}),
          brackets: existingBrackets.filter((_, j) => j !== bracketIndex),
        };
      });
      updateDivisions(next);
    };

    const updateBracket = (divisionIndex, bracketIndex, key, value) => {
      const next = divisions.map((division, i) => {
        if (i !== divisionIndex) return division;
        const existingBrackets = Array.isArray(division?.brackets) ? division.brackets : [];
        return {
          ...(division || {}),
          brackets: existingBrackets.map((bracket, j) => {
            if (j !== bracketIndex) return bracket;
            return { ...(bracket || {}), [key]: value };
          }),
        };
      });
      updateDivisions(next);
    };

    const updateBracketFields = (divisionIndex, bracketIndex, patch) => {
      const next = divisions.map((division, i) => {
        if (i !== divisionIndex) return division;
        const existingBrackets = Array.isArray(division?.brackets) ? division.brackets : [];
        return {
          ...(division || {}),
          brackets: existingBrackets.map((bracket, j) => {
            if (j !== bracketIndex) return bracket;
            return { ...(bracket || {}), ...(patch || {}) };
          }),
        };
      });
      updateDivisions(next);
    };

    const toggleBracketApaLevel = (divisionIndex, bracketIndex, level) => {
      const bracket = divisions[divisionIndex]?.brackets?.[bracketIndex] || {};
      const current = Array.isArray(bracket.apaLevels) ? bracket.apaLevels : [];
      const exists = current.includes(level);
      const next = exists ? current.filter((value) => value !== level) : current.concat(level);
      updateBracket(divisionIndex, bracketIndex, "apaLevels", next);
    };

    const getApaLevelsForGameType = (gameType) => {
      if (gameType === "9_ball") return [1, 2, 3, 4, 5, 6, 7, 8, 9];
      return [2, 3, 4, 5, 6, 7];
    };

    const apaGameTypeOptions = [
      { value: "", label: "Not set" },
      { value: "8_ball", label: "8-Ball (levels 2-7)" },
      { value: "fast_8", label: "Fast 8 (levels 2-7)" },
      { value: "9_ball", label: "9-Ball (levels 1-9)" },
      { value: "10_ball", label: "10-Ball" },
      { value: "straight_pool", label: "Straight Pool" },
      { value: "one_pocket", label: "One Pocket" },
      { value: "bank_pool", label: "Bank Pool" },
      { value: "snooker", label: "Snooker" }
    ];

    const fargoGameTypeOptions = [
      { value: "", label: "Not set" },
      { value: "8_ball", label: "8-Ball" },
      { value: "fast_8", label: "Fast 8" },
      { value: "9_ball", label: "9-Ball" },
      { value: "10_ball", label: "10-Ball" },
      { value: "straight_pool", label: "Straight Pool" },
      { value: "one_pocket", label: "One Pocket" },
      { value: "bank_pool", label: "Bank Pool" },
      { value: "snooker", label: "Snooker" }
    ];

    return h(
      "div",
      { className: "hd-modal" },
      h("div", {
        className: "hd-modal__backdrop",
        onClick: isSaving ? undefined : closeTournamentModal,
      }),
      h(
        "button",
        {
          type: "button",
          className: "hd-modal__close",
          onClick: isSaving ? undefined : closeTournamentModal,
          disabled: isSaving,
          "aria-label": "Close modal",
        },
        "×"
      ),
      h(
        "div",
        { className: "hd-modal__panel" },
        h(
          "div",
          { className: "hd-card hd-card--form" },
          h("h3", null, tournamentForm.id ? "Edit Tournament" : "Create Tournament"),
          tournamentForm.id
            ? h(
              "div",
              { className: "hd-field", style: "margin-bottom: 10px;" },
              h("label", { className: "hd-label" }, isGlobalCustomer ? "Internal Status" : "Status"),
              h(
                "select",
                {
                  className: "hd-input",
                  value: isGlobalCustomer
                    ? (() => {
                      const normalized = String(tournamentForm.status || "").trim().toLowerCase();
                      if (normalized === "reviewed") return "verified";
                      return normalized || "pending_review";
                    })()
                    : ((String(tournamentForm.status || "").toLowerCase() === "cancelled") ? "cancelled" : "active"),
                  onChange: function (event) {
                    const selected = event.target.value || (isGlobalCustomer ? "pending_review" : "active");
                    if (isGlobalCustomer) {
                      updateForm("status", selected);
                    } else {
                      updateForm("status", selected === "cancelled" ? "cancelled" : "active");
                    }
                  },
                },
                isGlobalCustomer
                  ? [
                    h("option", { value: "pending_review" }, "Pending review"),
                    h("option", { value: "verified" }, "Verified"),
                    h("option", { value: "certified" }, "Certified"),
                    h("option", { value: "cancelled" }, "Cancelled"),
                    h("option", { value: "deleted" }, "Deleted"),
                  ]
                  : [
                    h("option", { value: "active" }, "Active"),
                    h("option", { value: "cancelled" }, "Cancelled"),
                  ]
              )
            )
            : null,
          h(
            "div",
            { className: "hd-tabs" },
            h(
              "button",
              {
                type: "button",
                className: `hd-tab ${tournamentStep === 1 ? "is-active" : ""}`,
                onClick: function () {
                  setTournamentStep(1);
                },
              },
              "Basics"
            ),
            h(
              "button",
              {
                type: "button",
                className: `hd-tab ${tournamentStep === 2 ? "is-active" : ""}`,
                onClick: function () {
                  setTournamentStep(2);
                },
              },
              "Advanced"
            )
          ),
          tournamentStep === 1
            ? h(
              "div",
              null,
              isGlobalCustomer
                ? h(
                  "div",
                  {
                    className: "hd-field",
                    style: "border: 1px solid #dbeafe; border-radius: 10px; padding: 12px; background: #f8fbff;",
                  },
                  h(
                    "div",
                    { style: "display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;" },
                    h("div", { className: "hd-label", style: "margin: 0; font-weight: 700; color: #1e3a8a;" }, "SEO (Global Only)"),
                    h("span", { style: "font-size: 12px; color: #64748b;" }, "Manual values override auto SEO")
                  ),
                  h("label", { className: "hd-label" }, "Meta Title"),
                  h("input", {
                    className: "hd-input",
                    maxLength: 160,
                    placeholder: "Custom meta title (max 160)",
                    value: tournamentForm.metaTitle || "",
                    onInput: function (event) {
                      updateForm("metaTitle", event.target.value);
                    },
                  }),
                  h(
                    "div",
                    { style: "font-size: 12px; color: #64748b; margin: 4px 0 10px;" },
                    `${String(tournamentForm.metaTitle || "").length}/160`
                  ),
                  h("label", { className: "hd-label" }, "Meta Description"),
                  h("textarea", {
                    className: "hd-input",
                    rows: 3,
                    maxLength: 160,
                    placeholder: "Custom meta description (max 160)",
                    value: tournamentForm.metaDescription || "",
                    onInput: function (event) {
                      updateForm("metaDescription", event.target.value);
                    },
                  }),
                  h(
                    "div",
                    { style: "font-size: 12px; color: #64748b; margin-top: 4px;" },
                    `${String(tournamentForm.metaDescription || "").length}/160`
                  )
                )
                : null,
              h(
                "div",
                { className: getFieldClassName("profileId") },
                h("label", { className: "hd-label" }, "Location"),
                h(
                  "div",
                  { className: "hd-location-filters", style: "margin: 0 0 8px;" },
                  h(
                    "div",
                    { className: "hd-grid" },
                    h(
                      "div",
                      { className: "hd-field" },
                      h("label", { className: "hd-label" }, "Search"),
                      h("input", {
                        className: "hd-input",
                        value: tournamentLocationFormQuery,
                        placeholder: "Type to filter locations",
                        onInput: function (event) {
                          setTournamentLocationFormQuery(event.target.value);
                        },
                      })
                    ),
                    h(
                      "div",
                      { className: "hd-field" },
                      h("label", { className: "hd-label" }, "State"),
                      h("input", {
                        className: "hd-input",
                        value: getStateDisplayName(tournamentLocationFormStateFilter),
                        placeholder: "State (e.g., OH, Colorado)",
                        list: "tournamentLocationStateOptions",
                        onInput: function (event) {
                          const value = event.target.value;
                          const abbrev = getStateAbbrev(value);
                          setTournamentLocationFormStateFilter(abbrev || "");
                        },
                        onBlur: function () {
                          const abbrev = getStateAbbrev(getStateDisplayName(tournamentLocationFormStateFilter));
                          if (!abbrev) setTournamentLocationFormStateFilter("");
                        },
                      }),
                      h(
                        "datalist",
                        { id: "tournamentLocationStateOptions" },
                        (locationStateOptions || []).map((value) => {
                          const abbrev = getStateAbbrev(value) || String(value || "").trim();
                          const display = getStateDisplayName(abbrev || value);
                          const optionValue = display || abbrev;
                          return h("option", { key: optionValue, value: optionValue, label: abbrev || display });
                        })
                      )
                    ),
                    h(
                      "div",
                      { className: "hd-field" },
                      h("label", { className: "hd-label" }, "City"),
                      h("input", {
                        className: "hd-input",
                        value: tournamentLocationFormCityFilter,
                        placeholder: "City",
                        list: "tournamentLocationCityOptions",
                        onInput: function (event) {
                          setTournamentLocationFormCityFilter(event.target.value);
                        },
                        onBlur: function () {
                          setTournamentLocationFormCityFilter(String(tournamentLocationFormCityFilter || "").trim());
                        },
                      }),
                      h(
                        "datalist",
                        { id: "tournamentLocationCityOptions" },
                        (locationCityOptions || []).map((value) =>
                          h("option", { key: value, value: value })
                        )
                      )
                    )
                  )
                ),
                h(
                  "select",
                  {
                    className: getInputClassName("profileId"),
                    value: tournamentProfileId,
                    onChange: function (event) {
                      setTournamentProfileId(event.target.value);
                      clearFieldError("profileId");
                    },
                  },
                  h("option", { value: "" }, "Select a location"),
                  (filteredProfiles || []).map((profile) =>
                    h("option", { key: profile.id, value: profile.id }, profile.title || profile.slug || profile.id)
                  )
                ),
                renderFieldError("profileId")
              ),
              h(
                "div",
                { className: "hd-grid" },
                h(
                  "div",
                  { className: getFieldClassName("title") },
                  h("label", { className: "hd-label" }, "Title"),
                  h("input", {
                    className: getInputClassName("title"),
                    value: tournamentForm.title,
                    onInput: function (event) {
                      updateForm("title", event.target.value);
                    },
                  }),
                  renderFieldError("title")
                ),
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Series"),
                  h("input", {
                    className: "hd-input",
                    value: tournamentForm.series,
                    onInput: function (event) {
                      updateForm("series", event.target.value);
                    },
                  })
                )
              ),
              h(
                "div",
                { className: "hd-grid" },
                h(
                  "div",
                  { className: getFieldClassName("tournamentStartDate") },
                  h("label", { className: "hd-label" }, "Start date"),
                  (() => {
                    const startParts = splitDateTimeParts(tournamentForm.tournamentStartDate);
                    return h(
                      "div",
                      { style: "display:flex;gap:8px;" },
                      h("input", {
                        className: getInputClassName("tournamentStartDate"),
                        style: "flex:1;",
                        type: "date",
                        value: startParts.date,
                        onInput: function (event) {
                          const nextDate = String(event.target.value || "").trim();
                          const nextValue = combineDateTimeParts(nextDate, startParts.time, "23:59");
                          updateForm("tournamentStartDate", nextValue);
                        },
                      }),
                      h("input", {
                        className: getInputClassName("tournamentStartDate"),
                        style: "width:130px;",
                        type: "time",
                        value: startParts.time,
                        onInput: function (event) {
                          const nextTime = String(event.target.value || "").trim();
                          const nextValue = combineDateTimeParts(startParts.date, nextTime, "23:59");
                          updateForm("tournamentStartDate", nextValue);
                        },
                      })
                    );
                  })(),
                  renderFieldError("tournamentStartDate")
                ),
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "End date"),
                  (() => {
                    const endParts = splitDateTimeParts(tournamentForm.tournamentEndDate);
                    return h(
                      "div",
                      { style: "display:flex;gap:8px;" },
                      h("input", {
                        className: "hd-input",
                        style: "flex:1;",
                        type: "date",
                        value: endParts.date,
                        onInput: function (event) {
                          const nextDate = String(event.target.value || "").trim();
                          const nextValue = combineDateTimeParts(nextDate, endParts.time, "23:59");
                          updateForm("tournamentEndDate", nextValue);
                        },
                      }),
                      h("input", {
                        className: "hd-input",
                        style: "width:130px;",
                        type: "time",
                        value: endParts.time,
                        onInput: function (event) {
                          const nextTime = String(event.target.value || "").trim();
                          const nextValue = combineDateTimeParts(endParts.date, nextTime, "23:59");
                          updateForm("tournamentEndDate", nextValue);
                        },
                      })
                    );
                  })()
                )
              ),
              h(
                "div",
                { className: "hd-grid" },
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Registration Start"),
                  (() => {
                    const registrationStartParts = splitDateTimeParts(spec.registration_start || spec.registrationStart || "");
                    return h(
                      "div",
                      { style: "display:flex;gap:8px;" },
                      h("input", {
                        className: "hd-input",
                        style: "flex:1;",
                        type: "date",
                        value: registrationStartParts.date,
                        onInput: function (event) {
                          const nextDate = String(event.target.value || "").trim();
                          const nextValue = combineDateTimeParts(nextDate, registrationStartParts.time, "00:00");
                          updateSpec("registration_start", nextValue);
                        },
                      }),
                      h("input", {
                        className: "hd-input",
                        style: "width:130px;",
                        type: "time",
                        value: registrationStartParts.time,
                        onInput: function (event) {
                          const nextTime = String(event.target.value || "").trim();
                          const nextValue = combineDateTimeParts(registrationStartParts.date, nextTime, "00:00");
                          updateSpec("registration_start", nextValue);
                        },
                      })
                    );
                  })()
                ),
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Registration End"),
                  (() => {
                    const registrationEndParts = splitDateTimeParts(
                      spec.registration_end || spec.registrationEnd || spec.entry_deadline || spec.entryDeadline || ""
                    );
                    return h(
                      "div",
                      { style: "display:flex;gap:8px;" },
                      h("input", {
                        className: "hd-input",
                        style: "flex:1;",
                        type: "date",
                        value: registrationEndParts.date,
                        onInput: function (event) {
                          const nextDate = String(event.target.value || "").trim();
                          const nextValue = combineDateTimeParts(nextDate, registrationEndParts.time, "23:59");
                          updateSpec("registration_end", nextValue);
                          updateSpec("entry_deadline", nextValue);
                        },
                      }),
                      h("input", {
                        className: "hd-input",
                        style: "width:130px;",
                        type: "time",
                        value: registrationEndParts.time,
                        onInput: function (event) {
                          const nextTime = String(event.target.value || "").trim();
                          const nextValue = combineDateTimeParts(registrationEndParts.date, nextTime, "23:59");
                          updateSpec("registration_end", nextValue);
                          updateSpec("entry_deadline", nextValue);
                        },
                      })
                    );
                  })()
                )
              ),
              h(
                "div",
                { className: "hd-grid" },
                h(
                  "div",
                  { className: getFieldClassName("ratingSystemType") },
                  h("label", { className: "hd-label" }, "Rating system"),
                  h(
                    "select",
                    {
                      className: getInputClassName("ratingSystemType"),
                      value: tournamentForm.ratingSystemType,
                      onChange: function (event) {
                        updateForm("ratingSystemType", event.target.value);
                      },
                    },
                    ratingOptions.map((option) => h("option", { key: option.value, value: option.value }, option.label))
                  ),
                  renderFieldError("ratingSystemType")
                ),
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Game"),
                  h(
                    "select",
                    {
                      className: "hd-input",
                      value: String(spec.game || spec.game_type || ""),
                      onChange: function (event) {
                        updateSpec("game", event.target.value);
                        updateSpec("game_type", event.target.value);
                      },
                    },
                    h("option", { value: "" }, "Select game"),
                    gameOptions.map((option) => h("option", { key: option, value: option }, option))
                  )
                ),
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Format Type"),
                  h(
                    "select",
                    {
                      className: "hd-input",
                      value: getTournamentFormatType(),
                      onChange: function (event) {
                        setTournamentFormatType(event.target.value);
                      },
                    },
                    formatTypeOptions.map((option) =>
                      h("option", { key: option.value, value: option.value }, option.label)
                    )
                  )
                ),
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Handicap"),
                  h(
                    "select",
                    {
                      className: "hd-input",
                      value: getTournamentHandicapMethod(),
                      onChange: function (event) {
                        setTournamentHandicapMethod(event.target.value);
                      },
                    },
                    handicapMethodOptions.map((option) =>
                      h("option", { key: option.value, value: option.value }, option.label)
                    )
                  )
                ),
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Tables"),
                  h(
                    "select",
                    {
                      className: "hd-input",
                      value: normalizeTableSizeValue(
                        (Array.isArray(spec.table_size) ? spec.table_size[0] : (spec.table_size || spec.tables)) || ""
                      ),
                      onChange: function (event) {
                        const nextValue = normalizeTableSizeValue(event.target.value);
                        updateSpec("table_size", nextValue ? [nextValue] : []);
                      },
                    },
                    h("option", { value: "" }, "Select table size"),
                    tableOptions.map((option) => h("option", { key: option, value: option }, option))
                  )
                )
              ),
              h(
                "div",
                { className: "hd-grid" },
                h(
                  "div",
                  { className: getFieldClassName("buyIn") },
                  h("label", { className: "hd-label" }, "Buy-in (USD)"),
                  h("input", {
                    className: getInputClassName("buyIn"),
                    type: "number",
                    min: "0",
                    step: "1",
                    value: tournamentForm.buyIn,
                    onInput: function (event) {
                      updateForm("buyIn", event.target.value);
                    },
                  }),
                  renderFieldError("buyIn")
                ),
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Admin Fee (USD)"),
                  h("input", {
                    className: "hd-input",
                    type: "number",
                    min: "0",
                    step: "1",
                    value: tournamentForm.adminFee,
                    onInput: function (event) {
                      updateForm("adminFee", event.target.value);
                    },
                  })
                )
              ),
              h(
                "div",
                { className: getFieldClassName("description") },
                h("label", { className: "hd-label" }, "Description"),
                h("textarea", {
                  className: getInputClassName("description"),
                  rows: 4,
                  value: tournamentForm.description,
                  onInput: function (event) {
                    updateForm("description", event.target.value);
                  },
                }),
                renderFieldError("description")
              ),
              h(
                "div",
                { className: "hd-grid" },
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Organizer name"),
                  h("input", {
                    className: "hd-input",
                    value: tournamentForm.contactName || "",
                    placeholder: "Tournament director",
                    onInput: function (event) {
                      updateForm("contactName", event.target.value);
                    },
                  })
                ),
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Organizer phone"),
                  h("input", {
                    className: "hd-input",
                    type: "tel",
                    value: tournamentForm.contactPhone || "",
                    placeholder: "(555) 555-5555",
                    onInput: function (event) {
                      updateForm("contactPhone", event.target.value);
                    },
                  })
                ),
                h(
                  "div",
                  { className: getFieldClassName("contactEmail") },
                  h("label", { className: "hd-label" }, "Organizer email"),
                  h("input", {
                    className: getInputClassName("contactEmail"),
                    type: "email",
                    value: tournamentForm.contactEmail || "",
                    placeholder: "director@example.com",
                    onInput: function (event) {
                      updateForm("contactEmail", event.target.value);
                    },
                  }),
                  renderFieldError("contactEmail")
                )
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Gallery (max 10 images)"),
                h("input", {
                  className: "hd-input",
                  type: "file",
                  accept: "image/*",
                  multiple: true,
                  onChange: handleTournamentGallerySelect,
                }),
                tournamentPendingUploads > 0
                  ? h("div", { className: "hd-save-status" }, `Uploading ${tournamentPendingUploads} image(s)...`)
                  : null,
                h(
                  "div",
                  { className: "hd-chip-group" },
                  (tournamentGalleryPreviews || []).map((previewUrl, index) =>
                    h(
                      "div",
                      {
                        key: previewUrl + index,
                        className: "hd-card",
                        draggable: true,
                        onDragStart: function (event) {
                          event.dataTransfer.setData("text/plain", String(index));
                        },
                        onDragOver: function (event) {
                          event.preventDefault();
                        },
                        onDrop: function (event) {
                          event.preventDefault();
                          const fromIndex = Number(event.dataTransfer.getData("text/plain"));
                          reorderTournamentGallery(fromIndex, index);
                        },
                      },
                      h("img", { className: "hd-card__image", src: previewUrl, alt: "Gallery" }),
                      h(
                        "button",
                        {
                          type: "button",
                          className: "hd-card__link--danger",
                          onClick: function () {
                            removeTournamentGalleryImage(index);
                          },
                        },
                        "Remove"
                      )
                    )
                  )
                )
              ),
              tournamentForm.id
                ? h(
                  "div",
                  { className: "hd-field" },
                  h(
                    "label",
                    { className: "hd-label" },
                    h("input", {
                      type: "checkbox",
                      checked: tournamentForm.applyToSeries,
                      onChange: function (event) {
                        updateForm("applyToSeries", event.target.checked);
                      },
                    }),
                    " Apply edits to all upcoming tournaments in this series"
                  )
                )
                : null,
              h(
                "div",
                { className: "hd-field" },
                h(
                  "label",
                  { className: "hd-label" },
                  h("input", {
                    type: "checkbox",
                    checked: tournamentForm.seriesEnabled,
                    onChange: function (event) {
                      updateForm("seriesEnabled", event.target.checked);
                    },
                  }),
                  " Create recurring series"
                )
              ),
              tournamentForm.seriesEnabled
                ? h(
                  "div",
                  { className: "hd-grid" },
                  h(
                    "div",
                    { className: "hd-field" },
                    h("label", { className: "hd-label" }, "Occurrences (max 52)"),
                    h("input", {
                      className: "hd-input",
                      type: "number",
                      min: "1",
                      max: "52",
                      value: tournamentForm.seriesCount,
                      onInput: function (event) {
                        updateForm("seriesCount", event.target.value);
                      },
                    })
                  ),
                  h(
                    "div",
                    { className: "hd-field" },
                    h("label", { className: "hd-label" }, "Frequency"),
                    h(
                      "select",
                      {
                        className: "hd-input",
                        value: tournamentForm.seriesFrequency,
                        onChange: function (event) {
                          updateForm("seriesFrequency", event.target.value);
                        },
                      },
                      h("option", { value: "weekly" }, "Weekly"),
                      h("option", { value: "alternate" }, "Alternate Week"),
                      h("option", { value: "daily" }, "Daily"),
                      h("option", { value: "monthly" }, "Monthly")
                    )
                  ),
                  h(
                    "div",
                    { className: "hd-field" },
                    h("label", { className: "hd-label" }, "Interval"),
                    h("input", {
                      className: "hd-input",
                      type: "number",
                      min: "1",
                      value: tournamentForm.seriesInterval,
                      onInput: function (event) {
                        updateForm("seriesInterval", event.target.value);
                      },
                    })
                  ),
                  h(
                    "div",
                    { className: "hd-field" },
                    h("label", { className: "hd-label" }, "Days of week"),
                    h(
                      "div",
                      { className: "hd-chip-group" },
                      daysOfWeek.map((day) => {
                        const isActive = (tournamentForm.seriesDaysOfWeek || []).includes(day.value);
                        return h(
                          "button",
                          {
                            type: "button",
                            className: `hd-chip ${isActive ? "is-active" : ""}`,
                            onClick: function () {
                              const current = tournamentForm.seriesDaysOfWeek || [];
                              const next = isActive
                                ? current.filter((value) => value !== day.value)
                                : current.concat(day.value);
                              updateForm("seriesDaysOfWeek", next);
                            },
                          },
                          day.label
                        );
                      })
                    )
                  )
                )
                : null,
              h(
                "div",
                null,
                tournamentSaveStatus
                  ? h("div", { className: statusClassName }, tournamentSaveStatus)
                  : null,
                h(
                  "div",
                  { className: "hd-actions" },
                  tournamentForm.id
                    ? h(
                        "button",
                        {
                          type: "button",
                          className: "hd-save-btn",
                          style: "background:#2563eb; border-color:#2563eb;",
                          disabled: isSaving,
                          onClick: duplicateCurrentTournament,
                        },
                        "Duplicate"
                      )
                    : null,
                  tournamentForm.id
                    ? h(
                        "button",
                        {
                          type: "button",
                          className: "hd-save-btn",
                          style: "background:#b91c1c; border-color:#b91c1c; margin-right:auto;",
                          disabled: isSaving,
                          onClick: function () {
                            if (typeof setTournamentDeleteConfirmOpen === "function") {
                              setTournamentDeleteConfirmOpen(true);
                            }
                          },
                        },
                        "Delete Tournament"
                      )
                    : null,
                  h(
                    "button",
                    {
                      type: "button",
                      className: "hd-save-btn",
                      disabled: isSaving,
                      onClick: function () {
                        setTournamentStep(2);
                      },
                    },
                    "Advanced"
                  ),
                  h(
                    "button",
                    {
                      type: "button",
                      className: "hd-save-btn",
                      disabled: isSaving,
                      onClick: submitTournamentForm,
                    },
                    isSaving
                      ? [h("span", { className: "hd-spinner", "aria-hidden": "true" }), "Saving..."]
                      : "Save Tournament"
                  )
                )
              )
            )
            : h(
              "div",
              null,
              h("h3", null, "Advanced"),
              tournamentForm.id
                ? h(
                  "div",
                  { className: "hd-field" },
                  h(
                    "label",
                    { className: "hd-label" },
                    h("input", {
                      type: "checkbox",
                      checked: tournamentForm.applyToSeries,
                      onChange: function (event) {
                        updateForm("applyToSeries", event.target.checked);
                      },
                    }),
                    " Apply edits to all upcoming tournaments in this series"
                  )
                )
                : null,
              h(
                "div",
                { className: "hd-field" },
                h(
                  "label",
                  { className: "hd-label" },
                  h("input", {
                    type: "checkbox",
                    checked: tournamentForm.seriesEnabled,
                    onChange: function (event) {
                      updateForm("seriesEnabled", event.target.checked);
                    },
                  }),
                  " Create recurring series"
                )
              ),
              tournamentForm.seriesEnabled
                ? h(
                  "div",
                  { className: "hd-grid" },
                  h(
                    "div",
                    { className: "hd-field" },
                    h("label", { className: "hd-label" }, "Occurrences (max 52)"),
                    h("input", {
                      className: "hd-input",
                      type: "number",
                      min: "1",
                      max: "52",
                      value: tournamentForm.seriesCount,
                      onInput: function (event) {
                        updateForm("seriesCount", event.target.value);
                      },
                    })
                  ),
                  h(
                    "div",
                    { className: "hd-field" },
                    h("label", { className: "hd-label" }, "Frequency"),
                    h(
                      "select",
                      {
                        className: "hd-input",
                        value: tournamentForm.seriesFrequency,
                        onChange: function (event) {
                          updateForm("seriesFrequency", event.target.value);
                        },
                      },
                      h("option", { value: "weekly" }, "Weekly"),
                      h("option", { value: "alternate" }, "Alternate Week"),
                      h("option", { value: "daily" }, "Daily"),
                      h("option", { value: "monthly" }, "Monthly")
                    )
                  ),
                  h(
                    "div",
                    { className: "hd-field" },
                    h("label", { className: "hd-label" }, "Interval"),
                    h("input", {
                      className: "hd-input",
                      type: "number",
                      min: "1",
                      value: tournamentForm.seriesInterval,
                      onInput: function (event) {
                        updateForm("seriesInterval", event.target.value);
                      },
                    })
                  ),
                  h(
                    "div",
                    { className: "hd-field" },
                    h("label", { className: "hd-label" }, "Days of week"),
                    h(
                      "div",
                      { className: "hd-chip-group" },
                      daysOfWeek.map((day) => {
                        const isActive = (tournamentForm.seriesDaysOfWeek || []).includes(day.value);
                        return h(
                          "button",
                          {
                            type: "button",
                            className: `hd-chip ${isActive ? "is-active" : ""}`,
                            onClick: function () {
                              const current = tournamentForm.seriesDaysOfWeek || [];
                              const next = isActive
                                ? current.filter((value) => value !== day.value)
                                : current.concat(day.value);
                              updateForm("seriesDaysOfWeek", next);
                            },
                          },
                          day.label
                        );
                      })
                    )
                  )
                )
                : null,
              h(
                "div",
                { className: "hd-field" },
                h(
                  "div",
                  { className: "hd-actions", style: "justify-content: space-between; margin-bottom: 8px;" },
                  h("label", { className: "hd-label", style: "margin: 0;" }, "Divisions"),
                  h(
                    "button",
                    {
                      type: "button",
                      className: "hd-save-btn",
                      onClick: addDivision,
                    },
                    "+ Add Division"
                  )
                ),
                divisions.length === 0
                  ? h("div", { className: "hd-save-status" }, "No divisions yet. Add one or more divisions, then add brackets inside each division.")
                  : h(
                    "div",
                    { className: "hd-chip-group", style: "display: grid; gap: 10px;" },
                    divisions.map((division, divisionIndex) => {
                      const safeDivision = division || {};
                      const divisionBrackets = Array.isArray(safeDivision.brackets) ? safeDivision.brackets : [];
                      const ratingSystem = normalizeRatingSystemType(tournamentForm.ratingSystemType);
                      const isFargo = ratingSystem === "fargo_rate";
                      const isApa = ratingSystem === "apa_skill_level";

                      return h(
                        "div",
                        { key: `division-${divisionIndex}`, className: "hd-card", style: "padding: 12px;" },
                        h(
                          "div",
                          { className: "hd-actions", style: "justify-content: space-between; margin-bottom: 8px;" },
                          h("strong", null, safeDivision.name || `Division ${divisionIndex + 1}`),
                          h(
                            "button",
                            {
                              type: "button",
                              className: "hd-card__link--danger",
                              onClick: function () {
                                removeDivision(divisionIndex);
                              },
                            },
                            "Remove Division"
                          )
                        ),
                        h(
                          "div",
                          { className: "hd-field" },
                          h("label", { className: "hd-label" }, "Division Name"),
                          h("input", {
                            className: "hd-input",
                            value: safeDivision.name || "",
                            placeholder: `Division ${divisionIndex + 1}`,
                            onInput: function (event) {
                              updateDivision(divisionIndex, "name", event.target.value);
                            },
                          })
                        ),
                        h(
                          "div",
                          { className: "hd-grid" },
                          h(
                            "div",
                            { className: "hd-field" },
                            h("label", { className: "hd-label" }, "Division Type"),
                            h(
                              "select",
                              {
                                className: "hd-input",
                                value: safeDivision.divisionType || safeDivision.division_type || "",
                                onChange: function (event) {
                                  updateDivision(divisionIndex, "divisionType", event.target.value);
                                },
                              },
                              divisionTypeOptions.map((option) =>
                                h("option", { key: option.value, value: option.value }, option.label)
                              )
                            )
                          )
                        ),
                        h(
                          "div",
                          { className: "hd-grid" },
                          h(
                            "div",
                            { className: "hd-field" },
                            h("label", { className: "hd-label" }, "Handicap"),
                            h(
                              "select",
                              {
                                className: "hd-input",
                                value:
                                  safeDivision.handicapMethod ||
                                  safeDivision.handicap_method ||
                                  safeDivision.handicap ||
                                  "none",
                                onChange: function (event) {
                                  updateDivision(divisionIndex, "handicapMethod", event.target.value);
                                },
                              },
                              handicapMethodOptions.map((option) =>
                                h("option", { key: option.value, value: option.value }, option.label)
                              )
                            )
                          ),
                          h(
                            "div",
                            { className: "hd-field" },
                            h("label", { className: "hd-label" }, "Combined Skill Cap"),
                            h("input", {
                              className: "hd-input",
                              type: "number",
                              min: "0",
                              step: "1",
                              value: safeDivision.combinedSkillCap || safeDivision.combined_skill_cap || "",
                              placeholder: "e.g., 12",
                              onInput: function (event) {
                                updateDivision(divisionIndex, "combinedSkillCap", event.target.value);
                              },
                            })
                          )
                        ),
                        h(
                          "div",
                          { className: "hd-actions", style: "justify-content: space-between; margin: 8px 0;" },
                          h("label", { className: "hd-label", style: "margin: 0;" }, "Brackets"),
                          h(
                            "button",
                            {
                              type: "button",
                              className: "hd-save-btn",
                              onClick: function () {
                                addBracket(divisionIndex);
                              },
                            },
                            "+ Add Bracket"
                          )
                        ),
                        divisionBrackets.length === 0
                          ? h("div", { className: "hd-save-status" }, "No brackets in this division yet.")
                          : h(
                            "div",
                            { className: "hd-chip-group", style: "display: grid; gap: 10px;" },
                            divisionBrackets.map((bracket, bracketIndex) => {
                              const safeBracket = bracket || {};
                              const ratingMode = safeBracket.ratingMode || "to";
                              const gameType = resolveWinnerGameType(safeBracket);
                              const losersGameType = resolveLoserGameType(safeBracket, gameType);
                              const apaLevels = Array.isArray(safeBracket.apaLevels) ? safeBracket.apaLevels : [];
                              const apaOptions = getApaLevelsForGameType(gameType);

                              return h(
                                "div",
                                { key: `division-${divisionIndex}-bracket-${bracketIndex}`, className: "hd-card", style: "padding: 12px;" },
                                h(
                                  "div",
                                  { className: "hd-actions", style: "justify-content: space-between; margin-bottom: 8px;" },
                                  h("strong", null, safeBracket.name || `Bracket ${bracketIndex + 1}`),
                                  h(
                                    "button",
                                    {
                                      type: "button",
                                      className: "hd-card__link--danger",
                                      onClick: function () {
                                        removeBracket(divisionIndex, bracketIndex);
                                      },
                                    },
                                    "Remove"
                                  )
                                ),
                                h(
                                  "div",
                                  { className: "hd-grid" },
                                  h(
                                    "div",
                                    { className: "hd-field" },
                                    h("label", { className: "hd-label" }, "Bracket Name"),
                                    h("input", {
                                      className: "hd-input",
                                      value: safeBracket.name || "",
                                      placeholder: `Bracket ${bracketIndex + 1}`,
                                      onInput: function (event) {
                                        updateBracket(divisionIndex, bracketIndex, "name", event.target.value);
                                      },
                                    })
                                  ),
                                  isFargo
                                    ? h(
                                      "div",
                                      { className: "hd-field" },
                                      h("label", { className: "hd-label" }, "Fargo Min Rating"),
                                      h("input", {
                                        className: "hd-input",
                                        type: "number",
                                        min: "0",
                                        value: safeBracket.minRating || "",
                                        placeholder: "e.g. 200",
                                        onInput: function (event) {
                                          updateBracket(divisionIndex, bracketIndex, "minRating", event.target.value);
                                        },
                                      })
                                    )
                                    : null,
                                  isFargo
                                    ? h(
                                      "div",
                                      { className: "hd-field" },
                                      h("label", { className: "hd-label" }, "Range"),
                                      h(
                                        "select",
                                        {
                                          className: "hd-input",
                                          value: ratingMode,
                                          onChange: function (event) {
                                            updateBracket(divisionIndex, bracketIndex, "ratingMode", event.target.value);
                                          },
                                        },
                                        h("option", { value: "to" }, "To"),
                                        h("option", { value: "and_up" }, "And up")
                                      )
                                    )
                                    : null,
                                  isFargo && ratingMode === "to"
                                    ? h(
                                      "div",
                                      { className: "hd-field" },
                                      h("label", { className: "hd-label" }, "Fargo Max Rating"),
                                      h("input", {
                                        className: "hd-input",
                                        type: "number",
                                        min: "0",
                                        value: safeBracket.maxRating || "",
                                        placeholder: "e.g. 300",
                                        onInput: function (event) {
                                          updateBracket(divisionIndex, bracketIndex, "maxRating", event.target.value);
                                        },
                                      })
                                    )
                                    : null,
                                  isApa
                                    ? h(
                                      "div",
                                      { className: "hd-field" },
                                      h("label", { className: "hd-label" }, "Winner Game"),
                                      h(
                                        "select",
                                        {
                                          className: "hd-input",
                                          value: gameType,
                                          onChange: function (event) {
                                            updateBracketFields(divisionIndex, bracketIndex, {
                                              gameType: event.target.value,
                                              apaLevels: [],
                                            });
                                          },
                                        },
                                        apaGameTypeOptions.map((option) =>
                                          h("option", { key: option.value, value: option.value }, option.label)
                                        )
                                      )
                                    )
                                    : null,
                                  isApa
                                    ? h(
                                      "div",
                                      { className: "hd-field" },
                                      h("label", { className: "hd-label" }, "Loser Game"),
                                      h(
                                        "select",
                                        {
                                          className: "hd-input",
                                          value: losersGameType,
                                          onChange: function (event) {
                                            updateBracket(divisionIndex, bracketIndex, "losersGameType", event.target.value);
                                          },
                                        },
                                        apaGameTypeOptions.map((option) =>
                                          h("option", { key: option.value, value: option.value }, option.label)
                                        )
                                      )
                                    )
                                    : null,
                                  isFargo
                                    ? h(
                                      "div",
                                      { className: "hd-field" },
                                      h("label", { className: "hd-label" }, "Winner Game"),
                                      h(
                                        "select",
                                        {
                                          className: "hd-input",
                                          value: gameType,
                                          onChange: function (event) {
                                            updateBracket(divisionIndex, bracketIndex, "gameType", event.target.value);
                                          },
                                        },
                                        fargoGameTypeOptions.map((option) =>
                                          h("option", { key: option.value, value: option.value }, option.label)
                                        )
                                      )
                                    )
                                    : null,
                                  isFargo
                                    ? h(
                                      "div",
                                      { className: "hd-field" },
                                      h("label", { className: "hd-label" }, "Loser Game"),
                                      h(
                                        "select",
                                        {
                                          className: "hd-input",
                                          value: losersGameType,
                                          onChange: function (event) {
                                            updateBracket(divisionIndex, bracketIndex, "losersGameType", event.target.value);
                                          },
                                        },
                                        fargoGameTypeOptions.map((option) =>
                                          h("option", { key: option.value, value: option.value }, option.label)
                                        )
                                      )
                                    )
                                    : null
                                ),
                                isApa
                                  ? h(
                                    "div",
                                    { className: "hd-field" },
                                    h("label", { className: "hd-label" }, "APA Levels"),
                                    h(
                                      "div",
                                      { className: "hd-chip-group" },
                                      apaOptions.map((level) => {
                                        const active = apaLevels.includes(level);
                                        return h(
                                          "button",
                                          {
                                            type: "button",
                                            className: `hd-chip ${active ? "is-active" : ""}`,
                                            onClick: function () {
                                              toggleBracketApaLevel(divisionIndex, bracketIndex, level);
                                            },
                                          },
                                          String(level)
                                        );
                                      })
                                    )
                                  )
                                  : null,
                                isFargo
                                  ? h(
                                    "div",
                                    { className: "hd-save-status" },
                                    ratingMode === "to"
                                      ? `${safeBracket.minRating || "?"} - ${safeBracket.maxRating || "?"}`
                                      : `${safeBracket.minRating || "?"} and up`
                                  )
                                  : null,
                                h(
                                  "div",
                                  { className: "hd-grid" },
                                  h(
                                    "div",
                                    { className: "hd-field" },
                                    h("label", { className: "hd-label" }, "Table Size"),
                                    h(
                                      "select",
                                      {
                                        className: "hd-input",
                                        value: normalizeTableSizeValue(
                                          Array.isArray(safeBracket.tableSizes) ? (safeBracket.tableSizes[0] || "") : ""
                                        ),
                                        onChange: function (event) {
                                          const nextValue = normalizeTableSizeValue(event.target.value);
                                          updateBracket(divisionIndex, bracketIndex, "tableSizes", nextValue ? [nextValue] : []);
                                        },
                                      },
                                      h("option", { value: "" }, "Select table size"),
                                      tableOptions.map((option) => h("option", { key: option, value: option }, option))
                                    )
                                  ),
                                  h(
                                    "div",
                                    { className: "hd-field" },
                                    h("label", { className: "hd-label" }, "Max Players"),
                                    h("input", {
                                      className: "hd-input",
                                      type: "number",
                                      placeholder: "e.g., 64",
                                      value: safeBracket.maxPlayers || safeBracket.maxPlates || "",
                                      onInput: function (event) {
                                        updateBracket(divisionIndex, bracketIndex, "maxPlayers", event.target.value);
                                      },
                                    })
                                  ),
                                  h(
                                    "div",
                                    { className: "hd-field" },
                                    h("label", { className: "hd-label" }, "Winner Race"),
                                    h("input", {
                                      className: "hd-input",
                                      type: "number",
                                      placeholder: "e.g., 2",
                                      value: safeBracket.winnerRaces || safeBracket.winnerRace || "",
                                      onInput: function (event) {
                                        updateBracket(divisionIndex, bracketIndex, "winnerRaces", event.target.value);
                                      },
                                    })
                                  ),
                                  h(
                                    "div",
                                    { className: "hd-field" },
                                    h("label", { className: "hd-label" }, "Loser Race"),
                                    h("input", {
                                      className: "hd-input",
                                      type: "number",
                                      placeholder: "e.g., 1",
                                      value: safeBracket.loserRaces || safeBracket.loserRace || "",
                                      onInput: function (event) {
                                        updateBracket(divisionIndex, bracketIndex, "loserRaces", event.target.value);
                                      },
                                    })
                                  )
                                ),
                                h(
                                  "div",
                                  { className: "hd-field" },
                                  h(
                                    "label",
                                    { className: "hd-label" },
                                    h("input", {
                                      type: "checkbox",
                                      checked: Boolean(
                                        safeBracket.raceMayVaryBySkillLevel ||
                                        safeBracket.race_may_vary_by_skill_level
                                      ),
                                      onChange: function (event) {
                                        updateBracket(divisionIndex, bracketIndex, "raceMayVaryBySkillLevel", event.target.checked);
                                      },
                                    }),
                                    " Race may vary based on skill level"
                                  )
                                )
                              );
                            })
                          )
                      );
                    })
                  )
              ),
              h(
                "div",
                { className: "hd-grid" },
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Skill Levels"),
                  h(
                    "div",
                    { className: "hd-chip-group" },
                    skillLevelOptions.map((level) => {
                      const current = Array.isArray(spec.skill_levels) ? spec.skill_levels : [];
                      const isActive = current.includes(level);
                      return h(
                        "button",
                        {
                          type: "button",
                          className: `hd-chip ${isActive ? "is-active" : ""}`,
                          onClick: function () {
                            const next = isActive
                              ? current.filter((value) => value !== level)
                              : current.concat(level);
                            updateSpec("skill_levels", next);
                          },
                        },
                        level
                      );
                    })
                  ),
                  h("input", {
                    className: "hd-input",
                    value: (spec.skill_levels || []).join(", "),
                    placeholder: "Add custom levels, comma separated",
                    onInput: function (event) {
                      const values = event.target.value
                        .split(",")
                        .map((value) => value.trim())
                        .filter(Boolean);
                      updateSpec("skill_levels", values);
                    },
                  })
                ),
                null
              ),
              h(
                "div",
                { className: "hd-grid" },
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Prize Payouts"),
                  h("input", {
                    className: "hd-input",
                    value: spec.prize_payouts || "",
                    onInput: function (event) {
                      updateSpec("prize_payouts", event.target.value);
                    },
                  })
                )
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Side Action"),
                h(
                  "div",
                  { className: "hd-field", style: "display: grid; gap: 6px;" },
                  sideActionOptions.map((option) => {
                    const current = Array.isArray(spec.side_actions) ? spec.side_actions : [];
                    const isActive = current.includes(option.value);
                    return h(
                      "label",
                      { key: option.value, className: "hd-label" },
                      h("input", {
                        type: "checkbox",
                        checked: isActive,
                        onChange: function (event) {
                          const checked = Boolean(event.target.checked);
                          const next = checked
                            ? current.concat(option.value)
                            : current.filter((value) => value !== option.value);
                          updateSpec("side_actions", next);
                        },
                      }),
                      ` ${option.label}`
                    );
                  })
                ),
                h(
                  "label",
                  { className: "hd-label", style: "margin-top: 8px;" },
                  h("input", {
                    type: "checkbox",
                    checked: Boolean(spec.break_pot),
                    onChange: function (event) {
                      updateSpec("break_pot", event.target.checked);
                    },
                  }),
                  " Break Pot"
                )
              ),

              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Rules / Notes"),
                h("textarea", {
                  className: "hd-input",
                  rows: 3,
                  value: spec.rules || "",
                  onInput: function (event) {
                    updateSpec("rules", event.target.value);
                  },
                })
              ),
              h(
                "div",
                null,
                tournamentSaveStatus
                  ? h("div", { className: statusClassName }, tournamentSaveStatus)
                  : null,
                h(
                  "div",
                  { className: "hd-actions" },
                  tournamentForm.id
                    ? h(
                        "button",
                        {
                          type: "button",
                          className: "hd-save-btn",
                          style: "background:#2563eb; border-color:#2563eb;",
                          disabled: isSaving,
                          onClick: duplicateCurrentTournament,
                        },
                        "Duplicate"
                      )
                    : null,
                  tournamentForm.id
                    ? h(
                        "button",
                        {
                          type: "button",
                          className: "hd-save-btn",
                          style: "background:#b91c1c; border-color:#b91c1c; margin-right:auto;",
                          disabled: isSaving,
                          onClick: function () {
                            if (typeof setTournamentDeleteConfirmOpen === "function") {
                              setTournamentDeleteConfirmOpen(true);
                            }
                          },
                        },
                        "Delete Tournament"
                      )
                    : null,
                  h(
                    "button",
                    {
                      type: "button",
                      className: "hd-save-btn",
                      disabled: isSaving,
                      onClick: function () {
                        setTournamentStep(1);
                      },
                    },
                    "Basics"
                  ),
                  h(
                    "button",
                    {
                      type: "button",
                      className: "hd-save-btn",
                      disabled: isSaving,
                      onClick: submitTournamentForm,
                    },
                    isSaving
                      ? [h("span", { className: "hd-spinner", "aria-hidden": "true" }), "Saving..."]
                      : "Save Tournament"
                  )
                )
                ),
              tournamentDeleteConfirmOpen
                ? h(
                  "div",
                  { className: "hd-confirm" },
                  h("div", { className: "hd-confirm__backdrop" }),
                  h(
                    "div",
                    { className: "hd-confirm__panel" },
                    h("h3", { className: "hd-confirm__title" }, "Move Tournament To Deleted?"),
                    h(
                      "p",
                      { className: "hd-confirm__body" },
                      "This tournament will be moved to Deleted and unpublished."
                    ),
                    h(
                      "div",
                      { className: "hd-confirm__actions" },
                      h(
                        "button",
                        {
                          type: "button",
                          className: "hd-confirm__btn",
                          onClick: function () {
                            if (typeof setTournamentDeleteConfirmOpen === "function") {
                              setTournamentDeleteConfirmOpen(false);
                            }
                          },
                        },
                        "Cancel"
                      ),
                      h(
                        "button",
                        {
                          type: "button",
                          className: "hd-confirm__btn hd-confirm__btn--danger",
                          onClick: function () {
                            if (typeof confirmDeleteTournament === "function") confirmDeleteTournament();
                          },
                        },
                        "Delete"
                      )
                    )
                  )
                )
                : null
            )
        )
      )
    );
  };
})();
